import { useState } from "react";
import { Loader2, Mic, Sparkles, Square, Camera, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { studio, apiError, inputCls, btnPrimary } from "@/lib/studioApi";
import { useDictation } from "@/lib/useDictation";

export const AiReportCard = ({ interventionId, photosCount = 0, onApplied }) => {
  const [notes, setNotes] = useState("");
  const [usePhotos, setUsePhotos] = useState(true);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const { recording, transcribing, toggle } = useDictation((text) => setNotes((n) => (n ? `${n}\n${text}` : text)));
  const withPhotos = usePhotos && photosCount > 0;
  const ready = notes.trim().length >= 15 || withPhotos;

  const generate = async () => {
    if (!ready) return toast.error("Décrivez l'intervention en quelques mots ou ajoutez des photos.");
    setBusy(true);
    try {
      const { data } = await studio.post(`/interventions/${interventionId}/ai-report`, { notes, use_photos: withPhotos }, { timeout: 180000 });
      const { ai_suggestions = [], ai_photos_used = 0, ...iv } = data;
      onApplied(iv);
      setSuggestions(ai_suggestions);
      setNotes("");
      toast.success(`Rapport rédigé${ai_photos_used ? ` à partir de ${ai_photos_used} photo${ai_photos_used > 1 ? "s" : ""}` : ""} : travaux, matériel, constat et recommandations mis à jour. Relisez et ajustez.`);
    } catch (e) { toast.error(apiError(e, "Rédaction impossible.")); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="iv-ai-report" className="rounded-2xl border border-[#C9D4F4] bg-[#F6F8FD] p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-base font-semibold text-[#0F2645] inline-flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#3A5CC8]" /> Rédiger le rapport avec l'IA
        </h2>
        <span className="rounded-lg bg-white px-2.5 py-0.5 text-[11px] font-bold text-[#3A5CC8]">Photos + dictée</span>
      </div>
      <p className="mt-1 text-xs text-[#5C6A87]">
        Dictez ou tapez ce que vous avez fait, en vrac, et laissez l'IA lire vos photos : elle remplit les travaux, le matériel, les mesures, le constat et les recommandations, puis vous suggère ce qu'il manque.
      </p>
      <textarea
        data-testid="iv-ai-notes"
        rows={3}
        className={`${inputCls} mt-3 bg-white`}
        placeholder="Ex. : remplacé le groupe de sécurité du boiler, purgé les 6 radiateurs, pression remise à 1,5 bar, le client doit surveiller la fuite au robinet de la cuisine…"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      <label data-testid="iv-ai-use-photos" className={`mt-3 flex items-center gap-2.5 rounded-xl border px-3 py-2 text-sm ${photosCount ? "border-[#C9D4F4] bg-white text-[#0F2645]" : "border-dashed border-[#E2E7F7] text-[#93A0BC]"}`}>
        <input type="checkbox" checked={withPhotos} disabled={!photosCount} onChange={(e) => setUsePhotos(e.target.checked)} className="h-4 w-4 accent-[#3A5CC8]" />
        <Camera className="h-4 w-4 text-[#3A5CC8]" />
        {photosCount ? (photosCount > 1 ? `Lire les ${photosCount} photos de l'intervention (matériel, état, travaux visibles)` : "Lire la photo de l'intervention (matériel, état, travaux visibles)") : "Ajoutez des photos ci-dessous pour que l'IA les décrive dans le rapport"}
      </label>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="iv-ai-dictate"
          onClick={toggle}
          disabled={busy || transcribing}
          className={`inline-flex min-h-[40px] items-center gap-2 rounded-xl px-4 text-sm font-semibold transition-colors ${recording ? "bg-rose-600 text-white animate-pulse" : "border border-[#E2E7F7] bg-white text-[#0F2645] hover:border-[#3A5CC8]"}`}
        >
          {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          {transcribing ? "Transcription…" : recording ? "Arrêter la dictée" : "Dicter"}
        </button>
        <button type="button" data-testid="iv-ai-generate" onClick={generate} disabled={busy || !ready} className={btnPrimary}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {busy ? "Rédaction…" : notes.trim().length >= 15 ? "Rédiger le rapport" : "Rédiger depuis les photos"}
        </button>
      </div>
      {suggestions.length > 0 && (
        <div data-testid="iv-ai-suggestions" className="mt-4">
          <p className="mb-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#93A0BC]"><Lightbulb className="h-3.5 w-3.5 text-amber-500" /> Et ensuite ? Compléter le rapport</p>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((s, i) => (
              <button key={s} type="button" data-testid={`iv-ai-suggestion-${i + 1}`} onClick={() => setNotes((n) => (n ? `${n}\n${s} : ` : `${s} : `))} className="rounded-lg border border-[#E2E7F7] bg-white px-2.5 py-1.5 text-xs font-medium text-[#0F2645] transition-colors hover:border-[#3A5CC8] hover:text-[#2F4CA8]">
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
